import { registerApplication, start } from "single-spa";
import {
    constructApplications,
    constructRoutes,
    constructLayoutEngine,
} from "single-spa-layout";

import layout from "./microfrontend-layout.html";
import tenantLayout from "./microfrontend-layout.tenant.html";
import webLoader from "/assets/loader/jopit_web_loader.mp4";
import {FirebaseApplication, marketplaceLinkOf} from "@jopitnow/web-toolkit";
import {getValidTenant, marketplaceRedirectHrefFor} from "./tenant";
import {buyerSessionOptionsFor, environmentOf, hasBuyerSessionHint} from "./buyerSession";
import "./index.css";

const data = {
    loaders: {
        // `playsinline` no es opcional: en iPhone, Safari se lleva a su
        // reproductor nativo a pantalla completa cualquier <video> que arranque
        // sin ese atributo. El loader de 128px pasaba a ocupar toda la pantalla
        // con franjas negras arriba y abajo cada vez que un MFE tardaba lo
        // suficiente como para que el loader llegara a reproducirse.
        gifLoader: `<div class="gif_container">
            <video class="gif_video" autoplay muted loop playsinline>
                <source src=${webLoader} type="video/mp4">
            </video>
        </div>`,
    }
}

const config = {
    apiKey: process.env["FIREBASE_API_KEY"],
    authDomain: process.env["FIREBASE_AUTH_DOMAIN"],
    projectId: process.env["FIREBASE_PROJECT_ID"],
    storageBucket: process.env["FIREBASE_STORAGE_BUCKET"],
    messagingSenderId: process.env["FIREBASE_MESSAGING_SENDER_ID"],
    appId: process.env["FIREBASE_APP_ID"],
    measurementId: process.env["FIREBASE_MEASUREMENT_ID"]
};

// Configuración de analytics de producto, en un global y no en el bundle de cada
// MFE: los MFEs se despliegan por separado, así que cambiar esto no puede
// depender de redesplegar veinte repos.
//
// El project token de Mixpanel ya NO viaja acá. Es público por diseño y con él
// cualquiera puede inyectar eventos falsos o inflar la factura, así que los
// eventos salen contra un proxy propio (`jopit.web.analytics-proxy`) que inyecta
// el token del lado del server. `mixpanelToken` queda como relleno: el SDK
// necesita un string para arrancar y lo manda dentro del payload, donde el proxy
// lo reemplaza por el real.
//
// OJO: ese relleno siembra la clave de localStorage (`mp_jopit-web_mixpanel`)
// donde vive el distinct_id. Si se cambia, todos los visitantes pierden su
// identidad y se cuentan como usuarios nuevos.
const ingestHost = process.env["ANALYTICS_INGEST_HOST"];

window.__JOPIT_ANALYTIC__ = {
    // Sin host de ingesta el proveedor queda en no-op silencioso, que es lo que
    // queremos en local y en los tests.
    mixpanelToken: ingestHost ? "jopit-web" : undefined,
    apiHost: ingestHost,
    app: "marketplace",
    environment: process.env.NODE_ENV === "production" ? "production" : "staging",
    debug: false
};

await FirebaseApplication.init(config)

const environment = environmentOf(process.env.NODE_ENV);

// Los dos paquetes de sesión (`@jopitnow/web-session` y `@jopitnow/web-network`) viven en un chunk
// aparte del root-config, y se bajan UNA vez, cuando el primero de sus tres usos los pide: el canje
// del handoff, la reconciliación de la sesión de comprador o el watcher. Memoizado para que dos
// usos en el mismo arranque no dupliquen ni el import ni el consumer.
//
// Puede rechazar: es un `import()` y el chunk puede no bajar (CDN, red, un deploy a medias). Los
// llamadores lo tratan como lo que es —una mejora que hoy no se pudo cargar— y arrancan igual.
// Un intento fallido no queda memoizado, para que el siguiente uso vuelva a probar.
let sessionModules = null;
function loadSessionModules() {
    if (!sessionModules) {
        sessionModules = Promise.all([
            import("@jopitnow/web-session"),
            import("@jopitnow/web-network"),
        ]).then(([session, { FirebaseNetworkConsumer }]) => ({
            session,
            consumer: new FirebaseNetworkConsumer(environment === "production"),
        })).catch((error) => {
            sessionModules = null;
            throw error;
        });
    }
    return sessionModules;
}

// Canje del handoff de sesión cross-origin (`PLAN-DOMINIOS-VENDEDOR.md` §2.4). Un comprador que
// venía de `adidas.jopit.com.ar`, se logueó en el host canónico y volvió, llega acá con
// `?jopit_code=` en la URL. Cada host es un origin distinto, así que la sesión de Firebase no lo
// siguió: hay que canjear ese código por un custom token y firmar la sesión ANTES de `start()`,
// porque apenas monta un MFE empieza a pedir `onAuthRequest` y sin sesión eso es un 401.
//
// El prefiltro sobre `location.search` está a propósito antes del `import()`: mira una cadena que
// ya está en memoria y evita bajar el chunk en las cargas que no son ese único salto de vuelta
// desde el login. El módulo hace el parseo de verdad — esto no valida nada, sólo decide si vale
// la pena mirar.
//
// `redeemSessionHandoff` nunca rechaza: si el canje falla, la página igual arranca y el comprador
// queda deslogueado, que es exactamente el estado en el que ya estaba. Romper el arranque, en
// cambio, no degrada a nada.
if (window.location.search.includes("jopit_code")) {
    try {
        const { session, consumer } = await loadSessionModules();

        await session.redeemSessionHandoff({
            // El endpoint del canje es deliberadamente anónimo —el punto es establecer una sesión
            // donde todavía no hay ninguna—, así que va por `onAppRequest` y no por `onAuthRequest`.
            request: (endpoint, init) => consumer.onAppRequest(endpoint, init),
            // Nunca pasar acá algo que registre la URL: el código ya salió de la barra de direcciones
            // para no filtrarse por referrer ni por analytics, y volver a meterlo en un evento tiraría
            // ese trabajo a la basura.
            onError: (error) => console.warn("[jopit] session handoff failed", error),
        });
    } catch (error) {
        // `redeemSessionHandoff` nunca rechaza; lo único que puede caer acá es el chunk que no
        // bajó. Arrancar deslogueado es donde el comprador ya estaba; no arrancar no es nada.
        console.warn("[jopit] session handoff failed", error);
    }
}

// Modo tenant (`PLAN-DOMINIOS-VENDEDOR.md` §0.1, E1 puntos 1 y 6): un subdominio de tienda
// (`adidas.jopit.com.ar`) llega con `window.__JOPIT_TENANT__` ya inyectado por el Worker del
// borde. `getValidTenant()` es la única puerta: sin un tenant válido (el 100% del tráfico de
// hoy, o uno mal formado) se usa exactamente el layout de siempre, sin ninguna rama nueva.
const tenant = getValidTenant();

// Lo que es de Jopit vuelve a Jopit, incluso cuando se llega tipeando la URL.
//
// El layout de tenant ya no monta `/user/profile`, `/orders`, `/reviews`, `/tryon`,
// `/fittingroom` ni `/studio`, así que sin esto caerían en el `<route default>` y mostrarían la
// tienda con una URL que promete otra cosa. Con esto salen al host canónico llevando
// `return_origin`, que es el mismo destino al que ya apuntan todos los links.
//
// Va ANTES de construir el layout y de `start()`: una vez que un MFE monta ya hubo pintado y una
// entrada en el historial, y el redirect pasa a verse como un parpadeo. `replace` y no `assign`
// para no dejar la URL de ida en el historial — el botón de atrás volvería a redirigir.
let redirecting = false;
if (tenant) {
    const redirectHref = marketplaceRedirectHrefFor(marketplaceLinkOf, window.location);
    if (redirectHref) {
        redirecting = true;
        window.location.replace(redirectHref);
    }
}

// Sesión de comprador compartida (`PLAN-SESION-UNIFICADA.md` §3.2). Una cookie en `jopit.com.ar`
// hace que el login del marketplace valga en cada subdominio de tienda, y al revés que un logout
// en cualquiera cierre todos. En cada arranque este origin compara el hint `jopit_buyer_uid` con
// su propia sesión de Firebase y reconcilia; `reconcileBuyerSession` decide qué hacer según sea el
// marketplace (su sesión local es la verdad) o un tenant (el hint lo es), y nunca rechaza.
//
// ¿Bloquea `start()` o corre después? Depende de si cambia lo que se va a pintar. Con un hint en
// el frasco puede haber que firmar una sesión que falta o cerrar una que sobra, y hacerlo después
// de montar los MFEs es un parpadeo de "anónimo" o una sesión ajena a la vista: entonces se
// espera, como al handoff. Sin hint, lo único que puede pasar es el enroll del marketplace, que no
// se ve: arranca ya y se reconcilia en segundo plano. `hasBuyerSessionHint` mira una cadena en
// memoria, sin bajar el chunk — en las cargas sin hint, esto es todo lo que corre antes de start().
//
// Si la página ya se está yendo al host canónico, no vale la pena gastar en esto.
const buyerSessionOnError = (error) => console.warn("[jopit] buyer session", error);
const blockOnBuyerSession = !redirecting && hasBuyerSessionHint(document.cookie, environment);

if (blockOnBuyerSession) {
    try {
        const { session, consumer } = await loadSessionModules();
        await session.reconcileBuyerSession(buyerSessionOptionsFor(consumer, environment, buyerSessionOnError));
    } catch (error) {
        // `reconcileBuyerSession` nunca rechaza; esto es el chunk que no bajó. Con una cookie en el
        // frasco ese es ahora el caso COMÚN, así que un fallo acá tiene que costar exactamente lo
        // que costaba antes de esta feature: una carga deslogueada, nunca una página en blanco.
        buyerSessionOnError(error);
    }
}

const routes = constructRoutes(tenant ? tenantLayout : layout, data);

const applications = constructApplications({
    routes,
    loadApp({ name }) {
        return System.import(name);
    },
});

const layoutEngine = constructLayoutEngine({ routes, applications });

// Shared MFEs (login, shop-profile) are the same bundle mounted by both the
// shop and the marketplace root-configs, on the same routes — they can't tell
// which initiative they run under from the URL. We hand every app an
// `initiative` customProp so they can branch runtime behaviour (e.g. where the
// profile logout redirects, or the login post-auth default).
//
// `tenant` viaja al lado: `null` en el marketplace normal, o el objeto que inyectó el borde
// cuando `getValidTenant()` lo dio por válido. Hoy sólo lo consume esta elección de layout;
// se lo pasamos a los MFEs igual porque es el mismo seam que va a usar el chrome de tienda
// (header, theming) cuando se aborde E1b completo — no hay que volver a tocar este forEach.
applications.forEach((application) =>
    registerApplication({
        ...application,
        customProps: (name, location) => ({
            ...(typeof application.customProps === "function"
                ? application.customProps(name, location)
                : application.customProps),
            initiative: "marketplace",
            tenant,
        }),
    })
);

layoutEngine.activate();
start();

// El resto de la sesión de comprador, ya con la página arrancando: la reconciliación que no hizo
// falta bloquear (sin hint), y el watcher que la vuelve a correr cuando la pestaña vuelve a estar
// visible o en foco — así un logout hecho en otra pestaña se ve al mirar ésta, no al recargarla.
// Va en un idle callback para no competir con la descarga de los MFEs, que son el camino crítico.
if (!redirecting) {
    const whenIdle = window.requestIdleCallback ?? ((callback) => setTimeout(callback, 500));
    whenIdle(() => {
        loadSessionModules()
            .then(async ({ session, consumer }) => {
                const options = buyerSessionOptionsFor(consumer, environment, buyerSessionOnError);
                if (!blockOnBuyerSession) {
                    await session.reconcileBuyerSession(options);
                }
                session.watchBuyerSession(options);
            })
            .catch(buyerSessionOnError);
    });
}
